import React, { useEffect } from 'react';
import { AlertTriangle, AlertCircle, CheckCircle, X, ChevronRight, TrendingUp, TrendingDown, Sparkles } from 'lucide-react';
// import { LineChart, Line, ResponsiveContainer } from 'recharts';

export const Toast = ({ message, type, onClose }: { message: string, type: 'success' | 'error' | 'info', onClose: () => void }) => {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return (<div className={`fixed top-4 right-4 z-[100] flex items-center gap-3 px-4 py-3 rounded-2xl border shadow-[0_8px_30px_rgb(0,0,0,0.08)] animate-fade-in ${type === 'error' ? 'bg-red-50 text-red-900 border-red-100' : 'bg-white text-gray-900 border-gray-100'}`}>{type === 'error' ? <AlertCircle size={20} className="text-red-500" /> : <CheckCircle size={20} className="text-green-500" />} <span className="font-medium text-sm">{message}</span> <button onClick={onClose}><X size={16} className="opacity-30 hover:opacity-100 transition-opacity" /></button></div>);
};

export const ConfirmDialog = ({ message, onConfirm, onCancel }: { message: string, onConfirm: () => void, onCancel: () => void }) => (
  <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/20 backdrop-blur-md animate-fade-in"><div className="bg-white rounded-[2rem] p-8 w-full max-w-sm shadow-[0_20px_50px_rgb(0,0,0,0.1)] scale-100"><div className="flex flex-col items-center text-center mb-8"><div className="w-16 h-16 bg-amber-50 text-amber-500 rounded-full flex items-center justify-center mb-6 ring-8 ring-amber-500/5"><AlertTriangle size={28} strokeWidth={2.5} /></div><h3 className="text-xl font-bold text-gray-900">Bestätigung</h3><p className="text-base text-gray-500 mt-2 leading-relaxed">{message}</p></div><div className="flex gap-4"><button onClick={onCancel} className="flex-1 px-6 py-3.5 bg-gray-50 hover:bg-gray-100 text-gray-900 font-bold rounded-2xl text-sm transition-colors">Abbrechen</button><button onClick={onConfirm} className="flex-1 px-6 py-3.5 bg-black hover:bg-gray-800 text-white font-bold rounded-2xl text-sm transition-colors shadow-lg shadow-black/20">Bestätigen</button></div></div></div>
);

export const IOSCard = ({ title, children, className = "", noPadding = false, headerAction, onClick }: any) => (
  <div onClick={onClick} className={`app-glass-panel rounded-2xl overflow-hidden transition-colors duration-200 border border-white/40 dark:border-white/10 ${className} ${onClick ? 'cursor-pointer hover:bg-white/70 dark:hover:bg-white/10' : ''}`}>
    {title && (
      <div className="px-6 py-4 border-b border-white/40 dark:border-white/10 flex justify-between items-center bg-white/30 dark:bg-white/5 backdrop-blur-sm">
        <h3 className="font-bold text-slate-900 dark:text-white text-base tracking-tight">{title}</h3>
        {headerAction}
      </div>
    )}
    <div className={noPadding ? "" : "p-6"}>{children}</div>
  </div>
);

export const IOSBadge = ({ children, color = "blue", className = "" }: any) => {
  const colors: any = {
    blue: "bg-blue-50 text-blue-700 ring-1 ring-blue-500/20 dark:bg-blue-500/15 dark:text-blue-200",
    green: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-500/20 dark:bg-emerald-500/15 dark:text-emerald-200",
    red: "bg-red-50 text-red-700 ring-1 ring-red-500/20 dark:bg-red-500/15 dark:text-red-200",
    amber: "bg-amber-50 text-amber-700 ring-1 ring-amber-500/20 dark:bg-amber-500/15 dark:text-amber-200",
    gray: "bg-slate-100 text-slate-700 ring-1 ring-slate-400/20 dark:bg-slate-500/15 dark:text-slate-200",
    purple: "bg-violet-50 text-violet-700 ring-1 ring-violet-500/20 dark:bg-violet-500/15 dark:text-violet-200"
  };
  return <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wide ${colors[color]} ${className}`}>{children}</span>;
};

export const IOSButton = ({ children, onClick, variant = "primary", className = "", icon: Icon, disabled, type = "button" }: any) => {
  const variants: any = {
    primary: "bg-[#2F6BFF] hover:bg-[#1d59ff] text-white shadow-[0_10px_20px_rgba(47,107,255,0.22)] active:shadow-none",
    secondary: "bg-white hover:bg-[#F8FAFF] text-gray-900 border border-[#E9ECF2] shadow-sm",
    danger: "bg-red-500 hover:bg-red-600 text-white shadow-sm",
    ghost: "bg-transparent hover:bg-slate-100 text-gray-600"
  };
  return <button type={type} onClick={onClick} disabled={disabled} className={`px-5 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 active:scale-95 flex items-center justify-center gap-2 ${variants[variant]} ${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>{Icon && <Icon size={18} strokeWidth={2.5} />}{children}</button>;
};

export const IOSInput = (props: any) => <input {...props} className="w-full bg-white border border-[#E9ECF2] hover:border-[#DCE4F2] focus:border-blue-400/40 focus:ring-4 focus:ring-blue-500/10 rounded-2xl px-5 py-3.5 text-gray-900 outline-none text-sm font-medium transition-all duration-200 placeholder:text-gray-400" />;



export const DashboardHero = ({ name, performanceTrend = 0, dailyRevenue = "8.420 €", closureRate = "64,5%" }: any) => {
  const isAbove = performanceTrend >= 0;
  return (
    <div className={`rounded-[2.5rem] glass-panel p-10 lg:p-14 ${isAbove ? 'shadow-glow' : ''} relative overflow-hidden group min-h-[400px] flex flex-col justify-center`}>
      {/* Animated Mesh Gradients (subtle additions to the base background) */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100/50 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/2 transition-all duration-1000" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-100/60 blur-[100px] rounded-full transition-all duration-1000" />

      <div className="relative z-10 flex flex-col items-start gap-4">
        <div className="flex items-center gap-3 mb-2 px-6 py-2 bg-white/70 backdrop-blur-xl rounded-2xl border border-white animate-fade-in-up">
          <Sparkles size={20} className="text-blue-500" />
          <span className="text-[11px] font-black uppercase tracking-[0.3em]">Personal Insight</span>
        </div>

        <h2 className={`text-5xl lg:text-7xl font-black tracking-tighter mb-4 animate-fade-in-up transition-colors`} style={{ animationDelay: '0.1s' }}>
          Willkommen zurück,<br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-blue-500 to-cyan-500">{name}!</span>
        </h2>

        <p className={`text-lg max-w-lg leading-relaxed mb-4 font-medium animate-fade-in-up transition-colors opacity-80`} style={{ animationDelay: '0.2s' }}>
          Dein CRM ist bereit. Hier hast du alle aktuellen Kennzahlen im Blick. {isAbove ? "Dein Business wächst rasant." : "Bleib am Ball!"} Deine Performance liegt aktuell <span className={`font-black px-2 py-1 ${isAbove ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400' : 'bg-rose-500/20 text-rose-600 dark:text-rose-400'} rounded-xl backdrop-blur-lg border border-white/10 transition-colors`}>{Math.abs(performanceTrend)}% {isAbove ? 'über' : 'unter'} dem Ziel</span>.
        </p>
      </div>

      {/* Hero 3D Graphic */}
      <div className="absolute right-10 top-1/2 -translate-y-1/2 w-64 h-64 flex-shrink-0 z-10 hidden xl:block preserve-3d">
        <div className="absolute inset-0 bg-white/20 rounded-full blur-[60px] animate-pulse" />
        <div className="absolute inset-4 glass rounded-3xl rotate-6 animate-float shadow-2xl border-white/30" />
        <svg viewBox="0 0 200 200" className="w-full h-full text-white drop-shadow-2xl relative z-20">
          <circle cx="100" cy="100" r="45" fill="none" stroke="currentColor" strokeWidth="1" strokeDasharray="12,8" className="animate-spin-slow opacity-40" />
          <path d="M70,140 L100,80 L130,140 Z" fill="currentColor" fillOpacity="0.2" stroke="currentColor" strokeWidth="2" className="animate-pulse" />
          <circle cx="100" cy="80" r="18" fill="url(#heroGrad)" className="shadow-glow" />
          <defs>
            <linearGradient id="heroGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="white" />
              <stop offset="100%" stopColor="#818cf8" />
            </linearGradient>
          </defs>
          <rect x="150" y="40" width="30" height="30" rx="8" fill="currentColor" fillOpacity="0.3" transform="rotate(25 150 40)" className="animate-bounce-slow" />
        </svg>
      </div>
    </div>
  );
};

// SVG Minicharts with flowing gradients
const SvgAreaChart = ({ data, color }: any) => {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data.map((d: any) => d.value));
  const min = Math.min(...data.map((d: any) => d.value));
  const range = (max - min) || 1;
  const points = data.map((d: any, i: number) => {
    const x = (i / (data.length - 1)) * 100;
    const y = 38 - ((d.value - min) / range) * 34;
    return `${x},${y}`;
  }).join(' ');
  const id = React.useId().replace(/[^a-zA-Z0-9]/g, '');

  return (
    <svg viewBox="0 0 100 40" className="w-full h-full overflow-visible" preserveAspectRatio="none">
      <defs>
        <linearGradient id={`grad-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.5" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`M 0,40 L ${points} L 100,40 Z`} fill={`url(#grad-${id})`} className="animate-pulse" style={{ animationDuration: '4s' }} />
      <polyline points={points} fill="none" stroke={color} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" className="drop-shadow-sm" />
    </svg>
  );
};

const SvgBarChart = ({ data, color }: any) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map((d: any) => d.value));
  const barWidth = (100 / data.length);
  return (
    <svg viewBox="0 0 100 40" className="w-full h-full overflow-visible" preserveAspectRatio="none">
      {data.map((d: any, i: number) => {
        const h = Math.max(4, (d.value / max) * 40);
        return <rect key={i} x={i * barWidth + 1} y={40 - h} width={barWidth - 3} height={h} rx="3" fill={color} className="transition-all hover:scale-y-110 origin-bottom hover:fill-indigo-400 cursor-pointer" />;
      })}
    </svg>
  );
};

const SvgDonutChart = ({ value, color, isDark }: any) => {
  const r = 16, c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;
  return (
    <svg viewBox="0 0 40 40" className="w-20 h-20 -rotate-90 drop-shadow-xl">
      <circle cx="20" cy="20" r={r} fill="none" stroke={isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)"} strokeWidth="6" />
      <circle cx="20" cy="20" r={r} fill="none" stroke={color} strokeWidth="6" strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round" className="transition-all duration-1000 shadow-glow" style={{ transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)' }} />
      <text x="20" y="20" transform="rotate(90 20 20)" textAnchor="middle" alignmentBaseline="middle" className="text-[10px] font-black fill-slate-800 tabular-nums">{value}%</text>
    </svg>
  );
};

export const StatCard = ({ icon: Icon, label, value, color, onClick, size = 'default', trend, sparklineData, chartType = 'area', isDark }: any) => {
  const colors: any = {
    blue: { main: '#3b82f6', bg: 'bg-blue-500/10', text: 'text-blue-600', glow: 'shadow-blue-500/30' },
    emerald: { main: '#10b981', bg: 'bg-emerald-500/10', text: 'text-emerald-600', glow: 'shadow-emerald-500/30' },
    green: { main: '#10b981', bg: 'bg-emerald-500/10', text: 'text-emerald-600', glow: 'shadow-emerald-500/30' },
    red: { main: '#f43f5e', bg: 'bg-rose-500/10', text: 'text-rose-600', glow: 'shadow-rose-500/30' },
    indigo: { main: '#6366f1', bg: 'bg-indigo-500/10', text: 'text-indigo-600', glow: 'shadow-indigo-500/30' },
    amber: { main: '#f59e0b', bg: 'bg-amber-500/10', text: 'text-amber-600', glow: 'shadow-amber-500/30' }
  };
  const config = colors[color] || colors.blue;
  const IconToRender = Icon ?? AlertCircle;

  if (size === 'large') {
    return (
      <div onClick={onClick} className={`group relative flex flex-col bg-white border border-[#E9ECF2] rounded-2xl p-6 h-full transition-all duration-400 overflow-hidden ${onClick ? 'cursor-pointer hover:shadow-md hover:-translate-y-1' : ''}`}>
        <div className="flex justify-between items-start mb-4 z-10">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-bold tracking-widest uppercase text-slate-400">{label}</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className={`text-3xl font-bold tracking-tight ${isDark ? 'text-slate-100' : 'text-slate-900'} leading-none`}>{value}</span>
              {trend !== undefined && (
                <div className={`inline-flex items-center gap-1 text-[10px] font-bold mt-2 self-start px-2 py-0.5 rounded-full ${trend >= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                  {trend > 0 ? <TrendingUp size={12} strokeWidth={3} /> : <TrendingDown size={12} strokeWidth={3} />}
                  {Math.abs(trend)}%
                </div>
              )}
            </div>
          </div>
          <div className={`w-12 h-12 rounded-xl ${config.bg} ${config.text} flex items-center justify-center shadow-sm transform transition-all duration-500 group-hover:scale-110`}>
            <IconToRender size={24} strokeWidth={2.5} />
          </div>
        </div>

        {sparklineData && chartType !== 'donut' && (
          <div className="h-12 w-full mt-auto z-10 opacity-40 group-hover:opacity-60 transition-opacity">
            {chartType === 'bar' ? <SvgBarChart data={sparklineData} color={config.main} /> : <SvgAreaChart data={sparklineData} color={config.main} />}
          </div>
        )}
      </div>
    );
  }

  if (size === 'mini') {
    return (
      <div onClick={onClick} className="flex items-center gap-4 bg-white border border-[#E9ECF2] shadow-sm rounded-2xl p-5 hover:shadow-md transition-all duration-400 cursor-pointer active:scale-95 group relative">
        <div className={`w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-xl ${config.bg} ${config.text} shadow-sm group-hover:scale-110 transition-transform`}>
          <IconToRender size={24} strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-col">
            <span className={`text-2xl font-bold ${isDark ? 'text-slate-100' : 'text-slate-900'} tracking-tight leading-tight`}>{value}</span>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{label}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div onClick={onClick} className="group relative flex flex-col bg-white border border-[#E9ECF2] shadow-sm rounded-2xl p-6 h-full transition-all duration-400 hover:shadow-md cursor-pointer active:scale-95">
      <div className="flex justify-between items-start mb-2">
        <h3 className="text-[10px] font-bold tracking-widest uppercase text-slate-400">{label}</h3>
        {trend !== undefined && (
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${trend >= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <div className="flex items-end justify-between gap-4 mt-auto">
        <span className={`text-2xl font-bold tracking-tight ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>{value}</span>
        {sparklineData && (
          <div className="h-10 w-24 opacity-30 group-hover:opacity-50 transition-opacity">
            {chartType === 'bar' ? <SvgBarChart data={sparklineData} color={config.main} /> : <SvgAreaChart data={sparklineData} color={config.main} />}
          </div>
        )}
      </div>
    </div>
  );
};
